// Shared formatting + navigation helpers used by both `TaskLine` (the
// table-row layout used at md+) and `TaskCard` (the stacked layout used on
// narrow viewports). Keeping these in one place ensures both layouts stay in
// sync as we tweak how task fields render.

import { Link } from 'react-router-dom';
import { urlForTaskId } from '../../fleet-shared/Urls.mjs';
import { hasAssignee } from '../../fleet-shared/TaskAssigned.mjs';

// Database stores "no priority" as -1 (see `Database.updateTask` / Task
// detail page). Task rows / cards should not show that sentinel (or 0 /
// empty) as a visible number.
export function displayPriorityForTaskLine(priority) {
  if (priority == null || priority === -1 || priority === 0) {
    return '';
  }
  return priority;
}

export function formatCounterInterval(task) {
  if (task.counterInterval > 0) {
    return `then every ${task.counterInterval} ${task.itemCounter ? task.itemCounter.name : ''}`;
  }
  return '';
}

// Friendly recurrence label for a task row, e.g. "then quarterly" or
// "then every 45 days". Months take precedence over days (only one is set).
// Common month intervals get named labels; anything else falls back to a
// count.
export function formatInterval(task) {
  const months = task.monthsInterval;
  if (months > 0) {
    switch (months) {
    case 1:  return 'then monthly';
    case 3:  return 'then quarterly';
    case 6:  return 'then semi-annually';
    case 12: return 'then annually';
    default: return `then every ${months} months`;
    }
  }
  if (task.daysInterval > 0) {
    return `then every ${task.daysInterval} days`;
  }
  return '';
}

// Short interval phrase used inline (e.g. "(3 months from due date)").
// Returns '' when the task has no date-based recurrence.
export function intervalSummary(task) {
  const months = task.monthsInterval;
  if (months > 0) {
    return `${months} month${months === 1 ? '' : 's'}`;
  }
  if (task.daysInterval > 0) {
    return `${task.daysInterval} day${task.daysInterval === 1 ? '' : 's'}`;
  }
  return '';
}

// Navigate to the task detail page, preserving the originating bucket so
// the detail page knows which list it came from. Used by both layouts.
export function navigateToTask(navigate, task, tasksListBucket) {
  const url = urlForTaskId(task.itemId, task.id);
  if (tasksListBucket != null && tasksListBucket !== '') {
    navigate(url, { state: { tasksListBucket } });
  } else {
    navigate(url);
  }
}

// The url a row's assigned name should link to, or null when it stays text:
// no `assigneeLinkFor` from the caller (LogbookPickTask, the shared views), or
// nobody assigned (an empty name renders nothing, so there is nothing to link).
export function assigneeLinkUrl(task, assigneeLinkFor) {
  if (typeof assigneeLinkFor !== 'function' || !hasAssignee(task)) {
    return null;
  }
  return assigneeLinkFor(task) || null;
}

// The assigned-name cell content: a Link when the row has a url for it,
// otherwise the plain string. Both the row and the card handle click and
// keyboard on their container, so the link stops both from bubbling -- a click
// on the name must open the person's list, not the task, and Enter on the
// focused link must not fire the card's Enter handler as well.
export function AssigneeCell({ task, url }) {
  if (!url) {
    return task.assignedEmail;
  }
  return (
    <Link
      to={url}
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
    >
      {task.assignedEmail}
    </Link>
  );
}

// Vertical stack of small text lines. The first non-empty item is the
// primary value; the rest render as muted secondary detail. Falsy items
// (null, '', 0) are filtered out so we don't show empty sub-lines.
export const Stack = ({ items }) => (
  <>
    {items.filter(Boolean).map((item, index) => (
      <div key={index} className={index === 0 ? '' : 'text-body-secondary small'}>
        {item}
      </div>
    ))}
  </>
);
